import { AutocompleteInputChangeReason, debounce } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { LocationVehicleCategory } from '@abg/shared/constants';
import {
  LocationSuggestion,
  LocationSuggestionGroup,
} from '@abg/typings/biz/location.interface';
import { useBffClient } from '@abg/web-core/bff/data/client-provider';

import { userStoreSelector } from '../../../../store';
import { MINIMUM_SEARCH_QUERY_LENGTH } from '../../constants';
import {
  LocationSearchProperties,
  LocationSuggestionOption,
  SuggestionOptionsMode,
} from '../../location-search.types';
import { useGeolocationPermissions } from '../use-geolocation-permissions';

import {
  getCurrentReturnOptions,
  getRecentOptions,
  getSavedOptions,
  mapSearchSuggestionsOptions,
} from './location-search-options.utils';

type Properties = {
  formFieldName: LocationSearchProperties['name'];
  loadOptions: LocationSearchProperties['loadOptions'];
  loadCurrentOptions: LocationSearchProperties['loadCurrentOptions'];
  excludeVehicleCategories?: LocationVehicleCategory[] | undefined;
  allowGeolocation: LocationSearchProperties['allowGeolocation'];
  includeSavedLocations?: boolean;
  currentSearchMode: SuggestionOptionsMode;
  setCurrentSearchMode: (mode: SuggestionOptionsMode) => void;
  useCurrentLocationLabel: string;
  useCurrentLocationFoundErrorMessage: string;
  inputValue: string;
  setInputValue: (value: string) => void;
  selectedValue: LocationSuggestionOption | null;
  selectedGroup: LocationSuggestionGroup | null;
  handleClearField: () => void;
  setOpen: (open: boolean) => void;
  currentReservationLocation?: LocationSuggestionOption | null | undefined;
  includeRecentLocations?: boolean;
  inputReason?: AutocompleteInputChangeReason;
};

export const useLocationSearchOptions = ({
  formFieldName,
  loadOptions,
  loadCurrentOptions,
  allowGeolocation,
  includeSavedLocations = true,
  currentSearchMode,
  setCurrentSearchMode,
  useCurrentLocationLabel,
  useCurrentLocationFoundErrorMessage,
  inputValue,
  setInputValue,
  selectedValue,
  selectedGroup,
  handleClearField,
  setOpen,
  currentReservationLocation,
  includeRecentLocations = true,
  inputReason = 'input',
  excludeVehicleCategories,
}: Properties) => {
  const bffClient = useBffClient();
  const userProfile = userStoreSelector.use.userProfile();
  const { setError } = useFormContext() || {};
  const [options, setOptions] = useState<LocationSuggestionOption[]>([]);
  const [noMatchFound, setNoMatchFound] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    isGeoPermissionDenied,
    setIsGeoPermissionDenied,
    handleQueryPermissions,
  } = useGeolocationPermissions(allowGeolocation);

  // Use ref to always get the latest loadOptions function without recreating debounce
  const loadOptionsReference = useRef(loadOptions);
  loadOptionsReference.current = loadOptions;

  const debouncedLoadOptions = useCallback(
    debounce(
      async (
        inputValue: string,
        callback: (result: Awaited<ReturnType<typeof loadOptions>>) => void,
      ) => {
        const responseOptions = await loadOptionsReference.current(inputValue);

        callback(responseOptions);
      },
      250,
    ),
    [],
  );

  const handleLoadCurrentLocationOptions = async (
    latitude: number,
    longitude: number,
    callback: (result: Awaited<ReturnType<typeof loadOptions>>) => void,
  ) => {
    const responseOptions = await loadCurrentOptions({
      latitude,
      longitude,
    });

    callback(responseOptions);
  };

  const handleCurrentLocationOption = () => {
    if (isGeoPermissionDenied) {
      setLoading(false);
      return;
    }

    setOptions([]);
    setInputValue('');

    setTimeout(() => {
      setLoading(true);
      setOpen(true);
    }, 0);

    const options = {
      enableHighAccuracy: true,
      timeout: 10_000,
      maximumAge: 0,
    };

    navigator.geolocation.getCurrentPosition(
      (options) => {
        const { latitude, longitude } = options.coords;
        void handleLoadCurrentLocationOptions(
          latitude,
          longitude,
          (responseOptions) => {
            if (responseOptions === undefined) return; // for case when request was aborted
            if (responseOptions.length > 0) {
              setOptions(responseOptions);
              setCurrentSearchMode(SuggestionOptionsMode.GeoLocationOptions);
            } else {
              setOpen(false);
              setOptions([]);
              setInputValue('');
              setCurrentSearchMode(SuggestionOptionsMode.KeywordsOptions);

              setError?.(formFieldName, {
                type: 'manual',
                message: useCurrentLocationFoundErrorMessage,
              });
            }

            setLoading(false);
          },
        );
      },
      () => {
        setIsGeoPermissionDenied(true);
        setCurrentSearchMode(SuggestionOptionsMode.KeywordsOptions);
        setLoading(false);
        handleClearField();
      },
      options,
    );
  };

  useEffect(() => {
    // Do not send extra request when option is chosen
    if (inputValue === selectedValue?.locationName) {
      return;
    }

    if (inputValue === '') {
      setOptions([]);
      setLoading(false);
      return;
    }

    // Do not load options when input is set programmatically
    if (inputReason === 'reset') {
      return;
    }

    if (inputValue.length >= MINIMUM_SEARCH_QUERY_LENGTH) {
      setLoading(true);
      handleClearField();
      setNoMatchFound(false);

      void debouncedLoadOptions(inputValue, (responseOptions) => {
        if (responseOptions === undefined) return; // for case when request was aborted
        if (responseOptions.length > 0) {
          setOptions(responseOptions);
        } else {
          setOptions([]);
          setNoMatchFound(true);
        }

        setLoading(false);
        setCurrentSearchMode(SuggestionOptionsMode.KeywordsOptions);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inputValue,
    selectedValue,
    useCurrentLocationLabel,
    debouncedLoadOptions,
  ]);

  const currentLocationOption =
    !isGeoPermissionDenied &&
    currentSearchMode === SuggestionOptionsMode.KeywordsOptions
      ? [
          {
            locationName: useCurrentLocationLabel,
            suggestionTypeCode: 'CURRENT',
            suggestionGroup: 'CURRENT',
            identifier: 0,
          } as LocationSuggestionOption,
        ]
      : [];

  const [savedLocations, setSavedLocations] = useState<
    LocationSuggestionOption[]
  >([]);

  useEffect(() => {
    const fetchSavedLocations = async () => {
      const locations = userProfile?.preferences?.locations ?? [];

      if (!includeSavedLocations || locations.length === 0) return;

      try {
        const queryParams = new URLSearchParams();

        for (const location of locations) {
          queryParams.append('mnemonics', location.code);
        }

        const response = await bffClient.refdata<LocationSuggestion[]>(
          `/location/stations?${queryParams}`,
        );

        setSavedLocations(response ?? []);
      } catch {
        setSavedLocations([]);
      }
    };

    void fetchSavedLocations();
  }, [includeSavedLocations, userProfile?.preferences?.locations, bffClient]);

  const autocompleteOptions = useMemo(() => {
    const sessionOptions = includeRecentLocations
      ? getRecentOptions(excludeVehicleCategories)
      : [];
    const savedLocationsOptions = getSavedOptions(
      savedLocations,
      excludeVehicleCategories,
    );
    const currentReservationOption = currentReservationLocation
      ? getCurrentReturnOptions(currentReservationLocation)
      : [];

    return noMatchFound
      ? [{ locationName: '', typeCode: 'Error' } as LocationSuggestionOption]
      : loading || (selectedValue && Boolean(selectedValue.identifier))
        ? []
        : [
            ...currentLocationOption,
            ...currentReservationOption,
            ...mapSearchSuggestionsOptions(options),
            ...sessionOptions,
            ...savedLocationsOptions,
          ];
  }, [
    excludeVehicleCategories,
    noMatchFound,
    loading,
    selectedValue,
    currentLocationOption,
    options,
  ]);

  const groupedOptions = useMemo((): LocationSuggestionOption[] => {
    if (selectedGroup && inputValue.length > 0) {
      return autocompleteOptions.filter(
        (option) => option.suggestionGroup === selectedGroup,
      );
    }
    return autocompleteOptions;
  }, [autocompleteOptions, inputValue, selectedGroup]);

  useEffect(() => {
    if (
      inputValue === '' &&
      currentSearchMode === SuggestionOptionsMode.KeywordsOptions &&
      options.length > 0
    ) {
      debouncedLoadOptions.clear();
      setOptions([]);
      setLoading(false);
    }
  }, [inputValue, options.length, debouncedLoadOptions, currentSearchMode]);

  return {
    options: groupedOptions,
    loading,
    noMatchFound,
    isGeoPermissionDenied,
    setOptions,
    setNoMatchFound,
    handleCurrentLocationOption,
    handleQueryPermissions,
  };
};
